import { Controller } from "@hotwired/stimulus"

// Audience builder + live count for the EmailCampaign form (#528).
//
// Drives the four audience filters (active / pending / manual / search),
// shows the relevant extra input, serializes the selection into a hidden
// `audience_config` JSON field that the controller (#529) parses on submit,
// and fetches a LIVE recipient count (+ masked sample) from the backend's
// audience_preview endpoint (#535) — the count the operator confirms before
// dispatch. The backend re-resolves and re-checks on dispatch, so this number
// is informational, never the authority.
export default class extends Controller {
  static targets = [
    "filter", "query", "userIds",
    "searchField", "manualField",
    "config", "count", "sample", "previewButton"
  ]
  static values = { previewUrl: String }

  connect() {
    this.toggleFields()
    this.syncConfig()
  }

  // Filter radio changed: reveal the matching extra input and re-serialize.
  toggleFields() {
    const filter = this.selectedFilter()
    if (this.hasSearchFieldTarget) {
      this.searchFieldTarget.hidden = filter !== "search"
    }
    if (this.hasManualFieldTarget) {
      this.manualFieldTarget.hidden = filter !== "manual"
    }
    this.syncConfig()
  }

  // Serialize the current selection into the hidden audience_config field.
  // Uses the model's vocabulary (`filter`); the backend ResolverConfig maps it
  // to the resolver's `type`. Empty extras are omitted so the model's shape
  // validation stays happy.
  syncConfig() {
    const filter = this.selectedFilter()
    const config = { filter }

    if (filter === "search" && this.hasQueryTarget) {
      const q = this.queryTarget.value.trim()
      if (q) config.query = q
    }

    if (filter === "manual" && this.hasUserIdsTarget) {
      const ids = this.parseUserIds(this.userIdsTarget.value)
      if (ids.length) config.user_ids = ids
    }

    if (this.hasConfigTarget) {
      this.configTarget.value = JSON.stringify(config)
    }
  }

  // Parse a free-text list of user ids ("12, 34 56") into a unique integer
  // array. Non-numeric tokens are dropped.
  parseUserIds(raw) {
    return [...new Set(
      raw
        .split(/[\s,]+/)
        .map((t) => parseInt(t, 10))
        .filter((n) => Number.isInteger(n) && n > 0)
    )]
  }

  selectedFilter() {
    const checked = this.filterTargets.find((el) => el.checked)
    return checked ? checked.value : "active"
  }

  // Ask the backend how many recipients the current config resolves to. POSTs
  // the serialized config; renders count + masked sample. Failures degrade to a
  // generic, PII-free message — the dispatch is still gated server-side.
  async previewCount(event) {
    event.preventDefault()
    this.syncConfig()
    this.setBusy(true)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')
      const body = new URLSearchParams()
      body.set("audience_config", this.configTarget.value)

      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": token ? token.content : "",
          "Accept": "application/json"
        },
        body
      })

      if (!response.ok) {
        this.renderError()
        return
      }

      const data = await response.json()
      this.renderCount(data.count, data.sample || [])
    } catch (_e) {
      this.renderError()
    } finally {
      this.setBusy(false)
    }
  }

  renderCount(count, sample) {
    if (this.hasCountTarget) {
      this.countTarget.textContent = String(count)
      this.countTarget.dataset.count = String(count)
    }
    if (this.hasSampleTarget) {
      this.sampleTarget.textContent = sample.join(", ")
    }
  }

  renderError() {
    if (this.hasCountTarget) {
      this.countTarget.textContent = this.countTarget.dataset.errorLabel || "—"
      delete this.countTarget.dataset.count
    }
    if (this.hasSampleTarget) this.sampleTarget.textContent = ""
  }

  setBusy(busy) {
    if (this.hasPreviewButtonTarget) this.previewButtonTarget.disabled = busy
  }
}
